import "./InventoryBreakdownKPI.css";

import { ProjectIssuanceConstants } from "../../../../constants";
import { InventoryBreakdownMeasurement } from "../../../../models";
import { formatNumber } from "../../../../utils";
import { IssuanceStatusFilter, LoadingSpinner, TruncatedText } from "../../..";

interface InventoryBreakdownKPIProps {
  /** One entry per measurement the organisation holds units in. NEVER padded with empty ones. */
  measurements: InventoryBreakdownMeasurement[];
  /** Whether the aggregate is still in flight, so an empty card does not claim there is nothing. */
  isLoading?: boolean;
  title?: string;
  /** Selected issuance statuses. Omit `onStatusesChange` to render the card without the filter. */
  selectedStatuses?: string[];
  onStatusesChange?: (values: string[]) => void;
}

/**
 * The unit a tile's figure is counted in.
 *
 * A lookup rather than a literal because Nutrient Neutrality quantities are an annual rate elsewhere
 * in the product (D-4, "kg/yr") while this card's design labels every tile "units". The design is
 * implemented as drawn; routing it through here makes that a one-line change if Design decides the
 * NN tile should read kg/yr, instead of a literal buried in the JSX.
 */
export const getInventoryBreakdownUnitLabel = (unitMeasurement: string): string => {
  switch (unitMeasurement) {
    default:
      return "units";
  }
};

/**
 * The dashboard's inventory card: one tile per unit measurement the organisation actually holds,
 * derived from the aggregate response at render time.
 *
 * The tile set is NEVER a fixed list. An organisation holding only Carbon gets one tile, not three
 * with two empty ones, and a measurement this component has never heard of still renders - in the
 * documented grey fallback palette rather than unstyled.
 *
 * Replaces the measurement picker that packed its figure into the option key and read it back with
 * `unitsKey.split("-")[0]`: every measurement is now on screen at once, so there is nothing to pick.
 */
export const InventoryBreakdownKPI = ({
  measurements,
  isLoading = false,
  title = "Inventory breakdown",
  selectedStatuses,
  onStatusesChange,
}: InventoryBreakdownKPIProps): JSX.Element => (
  <div className="InventoryBreakdownKPI">
    <div className="InventoryBreakdownKPIHeader">
      <p className="body01">{title}</p>
      {onStatusesChange && <IssuanceStatusFilter values={selectedStatuses} onChange={onStatusesChange} />}
    </div>
    {measurements.length > 0 ? (
      <div className="InventoryBreakdownKPITiles">
        {measurements.map(({ unitMeasurement, total }) => {
          const colours = ProjectIssuanceConstants.getUnitMeasurementTileColours(unitMeasurement);

          return (
            <div
              className="InventoryBreakdownKPITile"
              key={unitMeasurement}
              style={{ backgroundColor: colours.background, color: colours.text }}
            >
              <div className="InventoryBreakdownKPITileMeasurement">
                <span className="InventoryBreakdownKPITileDot" style={{ backgroundColor: colours.dot }} />
                {/* Cut to one line so a long name ("Nutrient Neutrality") on a narrow tile doesn't push
                    the figure below it out of line with its neighbours. */}
                <TruncatedText text={unitMeasurement} textClass="body4" />
              </div>
              <h2>{formatNumber(total)}</h2>
              <p className="body5">{getInventoryBreakdownUnitLabel(unitMeasurement)}</p>
            </div>
          );
        })}
      </div>
    ) : (
      /*
        An empty list on its own does not mean the organisation holds nothing: the dashboards clear
        their own loading flag from the project-phase request, which can land before the issuance
        aggregate. Saying "no units" then would be a claim the card cannot support yet.

        The spinner matches the one the inventory chart surfaces draw while their own figures load.
      */
      <div className="InventoryBreakdownKPIEmpty">
        {isLoading ? <LoadingSpinner type="blue" size="medium" /> : <p className="body2 NoData">No units to display</p>}
      </div>
    )}
  </div>
);
